/**
 * Lead Capture Tracking Service
 * Manages progressive lead capture strategy for Chat.RealEstate
 */

interface LeadCaptureState {
  hasAcceptedAITerms: boolean;
  aiTermsAcceptedDate?: string;
  interactionCount: number;
  propertiesViewed: number;
  propertiesSaved: number;
  searchCount: number;
  sessionStartTime: number;
  lastPromptShown?: string;
  lastPromptTime?: number;
  emailCaptured: boolean;
  email?: string;
  conversionStage: 'anonymous' | 'terms_accepted' | 'email_captured' | 'account_created';
  dismissedPrompts: string[];
}

interface ConversionTrigger {
  id: string;
  type: 'soft' | 'medium' | 'strong';
  condition: (state: LeadCaptureState) => boolean;
  cooldownMinutes: number;
  maxDismissals: number;
}

class LeadCaptureTrackingService {
  private state: LeadCaptureState;
  private readonly STATE_KEY = 'lead_capture_state';
  private readonly SESSION_KEY = 'lead_capture_session';
  
  constructor() {
    this.state = this.loadState();
    this.initSession();
  }

  private loadState(): LeadCaptureState {
    const saved = localStorage.getItem(this.STATE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to load lead capture state:', e);
      }
    }
    
    return {
      hasAcceptedAITerms: false,
      interactionCount: 0,
      propertiesViewed: 0,
      propertiesSaved: 0,
      searchCount: 0,
      sessionStartTime: Date.now(),
      emailCaptured: false,
      conversionStage: 'anonymous',
      dismissedPrompts: []
    };
  }

  private saveState(): void {
    localStorage.setItem(this.STATE_KEY, JSON.stringify(this.state));
  }

  private initSession(): void {
    // Check if this is a new session
    const lastSession = sessionStorage.getItem(this.SESSION_KEY);
    if (!lastSession) {
      this.state.sessionStartTime = Date.now();
      sessionStorage.setItem(this.SESSION_KEY, Date.now().toString());
    }
  }

  // Tracking methods
  acceptAITerms(): void {
    this.state.hasAcceptedAITerms = true;
    this.state.aiTermsAcceptedDate = new Date().toISOString();
    this.state.conversionStage = 'terms_accepted';
    this.saveState();
  }

  trackInteraction(): void {
    this.state.interactionCount++;
    this.saveState();
  }

  trackPropertyView(propertyId: string): void {
    this.state.propertiesViewed++;
    this.saveState();
  }

  trackPropertySave(propertyId: string): void {
    this.state.propertiesSaved++;
    this.saveState();
  }

  trackSearch(criteria: any): void {
    this.state.searchCount++;
    this.saveState();
  }

  captureEmail(email: string): void {
    this.state.email = email;
    this.state.emailCaptured = true;
    this.state.conversionStage = 'email_captured';
    this.saveState();
  }

  dismissPrompt(promptId: string): void {
    if (!this.state.dismissedPrompts.includes(promptId)) {
      this.state.dismissedPrompts.push(promptId);
    }
    this.saveState();
  }

  recordPromptShown(promptId: string): void {
    this.state.lastPromptShown = promptId;
    this.state.lastPromptTime = Date.now();
    this.saveState();
  }

  // Conversion triggers
  private triggers: ConversionTrigger[] = [
    {
      id: 'soft_5_properties',
      type: 'soft',
      condition: (state) => 
        state.propertiesViewed >= 5 && 
        !state.emailCaptured &&
        state.hasAcceptedAITerms,
      cooldownMinutes: 30,
      maxDismissals: 2
    },
    {
      id: 'medium_saved_property',
      type: 'medium',
      condition: (state) => 
        state.propertiesSaved >= 1 && 
        !state.emailCaptured &&
        state.hasAcceptedAITerms,
      cooldownMinutes: 60,
      maxDismissals: 3
    },
    {
      id: 'strong_power_user',
      type: 'strong',
      condition: (state) => 
        (state.searchCount >= 3 || state.interactionCount >= 10) &&
        !state.emailCaptured &&
        state.hasAcceptedAITerms,
      cooldownMinutes: 1440, // 24 hours
      maxDismissals: 1
    }
  ];

  shouldShowAITerms(): boolean {
    return !this.state.hasAcceptedAITerms;
  }

  getNextPrompt(): { show: boolean; trigger?: ConversionTrigger } {
    // Don't show if email already captured
    if (this.state.emailCaptured) {
      return { show: false };
    }

    // Check cooldown
    if (this.state.lastPromptTime) {
      const minutesSinceLastPrompt = (Date.now() - this.state.lastPromptTime) / 60000;
      if (minutesSinceLastPrompt < 10) { // Global 10-minute cooldown
        return { show: false };
      }
    }

    // Find first matching trigger
    for (const trigger of this.triggers) {
      if (trigger.condition(this.state)) {
        // Check if dismissed too many times
        const dismissalCount = this.state.dismissedPrompts.filter(id => id === trigger.id).length;
        if (dismissalCount >= trigger.maxDismissals) {
          continue;
        }

        // Check trigger-specific cooldown
        if (this.state.lastPromptShown === trigger.id && this.state.lastPromptTime) {
          const minutesSince = (Date.now() - this.state.lastPromptTime) / 60000;
          if (minutesSince < trigger.cooldownMinutes) {
            continue;
          }
        }

        return { show: true, trigger };
      }
    }

    return { show: false };
  }

  getState(): LeadCaptureState {
    return { ...this.state };
  }

  getSessionDuration(): number {
    return Date.now() - this.state.sessionStartTime;
  }

  reset(): void {
    localStorage.removeItem(this.STATE_KEY);
    sessionStorage.removeItem(this.SESSION_KEY);
    this.state = this.loadState();
  }
}

// Export singleton instance
export default new LeadCaptureTrackingService();